import React, { useRef, useState } from "react";
import { RCCButton, RCCGrid, RCCHookToast, RCCIcon, RCCInput, RCCServicesApi, RCCTooltip } from "react-common-components";
import { IForwardPagination } from "react-common-components/dist/components/Pagination/interface";
import { useForm } from "react-hook-form";
import FiltersContainer from "src/components/FiltersContainer";
import PageTitle from "src/components/PageTitle";
import Pagination from "src/components/Pagination";
import { useTranslation } from "src/context/Translations/context";
import formatDate from "src/utils/formatDate";
import LovBusinessGroups from "src/components/Lov/BusinessGroups";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import { standardErrorMessages } from "src/utils/errors";
import ModalDescription from "src/components/ModalDescription";
import { IRefDescriptionModal } from "src/components/ModalDescription/interface";
import AppIcons from "src/components/AppIcons";
import { useAuth } from "src/context/Auth/context";
import getUrlSearchParams from "src/utils/getUrlSearchParams";
import { useLocation } from "react-router-dom";
import { ISystemConnectionsHistoryRef } from "./interface";
import ModalHistory from "./components/modalHistory";
import colUser from "./components/colUser";
import OverflowSpan, { StyledDivDescriptionAndAlertExclamation } from "./styles";


const SystemConnections: React.FC = () => {

	const query = useLocation();
	const objQueryParams = getUrlSearchParams(query.search);

	const { translate, currentLanguage } = useTranslation();
	const { confirm } = useConfirmModal();
	const {addToast} = RCCHookToast();
	const { superUser } = useAuth();

	const paginationRef = useRef<IForwardPagination>(null);
	const modalHistory = useRef<ISystemConnectionsHistoryRef>(null);
	const modalDescriptionRef = useRef<IRefDescriptionModal>(null);

	const [showFilters, setShowFilters] = useState(false);
	const [filters, setFilters] = useState<{ filter: string, group: string }>({ 
		filter: "", 
		group: objQueryParams?.businessgroup || "" 
	});

	const { 
		handleSubmit : handleSubmitFilters, 
		register : registerFilters, 
		watch : watchFilters, 
		reset: resetFilters ,
		control: controlFilters
	} = useForm();

	const submitFilters = (data: any) => {
		setFilters({ filter: data.filter, group: data.group?.value || data.group || "" });
		paginationRef.current?.reload();
	};
  
	const colDelete = (row: any) => (
		<>
			<RCCTooltip
				color="red"
				text={translate("Excluir")}
				position="left"	
			>
				<RCCButton
					color="red"
					icon={{
						name: "shutdown"
					}}
					layout="isRounded"
					onClick={() => deleteConnection(row.efs001Id, row.efs001Remoteaddr)}
				/>
			</RCCTooltip>		
		</>
	);

	const deleteConnection = async (id: NumberConstructor, name: string) => {
		confirm(
			translate("Exclusão de Conexão"), 
			`${translate("Deseja realmente excluir esta conexão")}: ${name}?`,
			async () => {

				await RCCServicesApi.deleteRequest({
					method: "Connections/Delete",
					urlParameters: {
						id
					}
				});

				addToast({
					title: translate("Sucesso"),
					description: translate("Conexão excluída com sucesso"),
					color: "green"
				});

				paginationRef.current?.reload();
				
			},
			(err) => {
				standardErrorMessages(err, translate("Erro ao excluir a conexão"), addToast);
			},
			true
		);
	};

	const colIp = (row: any) => (
		<RCCGrid
			container
			item
			alignItems="center"
			justifyContent="space-between"
		>
			<OverflowSpan title={row.efs001Remoteaddr}>
				{row.efs001Remoteaddr}
			</OverflowSpan>
			<StyledDivDescriptionAndAlertExclamation>
				<div className="infoCircleDescription-kr">
					<RCCIcon 
						name="exclamationCircle" 
						color="var(--grey)" 
						style={{ cursor: "pointer" }}
						size={16}
						onClick={() => modalDescriptionRef.current?.openModal(translate("Descrição"), row.efs001Useragent)} 
					/>
				</div>
			</StyledDivDescriptionAndAlertExclamation>
		</RCCGrid>
	);

	return (
		<>
			<PageTitle
				title={translate("Conexões do Sistema")}
				icon={() => <AppIcons name="configs" />}
			>
				<RCCTooltip
					text={translate("Histórico")}
					color="grey"
				>
					<RCCButton
						icon={{name: "history", color: "#FFF", size: 18}}
						color="grey"
						layout="isRounded"
						onClick={() => { 
							modalHistory.current?.openModal({
								group: filters.group
							}); 
						}}
						size="md"
					/>
				</RCCTooltip>
				<RCCTooltip
					text={translate("Filtros")}
					color={showFilters || filters.filter || filters.group ? "blue" : "grey"}
				>
					<RCCButton
						icon={{name: "filter", color: "#FFF", size: 16}}
						color={showFilters || filters.filter || filters.group ? "blue" : "grey"}
						layout="isRounded"
						size="md"
						onClick={() => setShowFilters((prevState) => !prevState)}
					/>
				</RCCTooltip>
			</PageTitle>

			{/* Filters */}
			{showFilters && (
				<FiltersContainer
					onSubmitFilters={handleSubmitFilters(submitFilters)}
					resetFilters={() => resetFilters({ group: "", filter: ""})}
				>
					<RCCGrid
						container
						item
						spacing={2}
					>
						{superUser && (
							<RCCGrid
								container 
								item
								xs={12}
								sm={6}
								md={4}
								lg={4}
							>
								<LovBusinessGroups 
									control={controlFilters}
									name="group" 
									defaultValue={filters.group ?? ""}
									label={translate("Grupo de Empresas")}
									submit="value and label"
								/>
							</RCCGrid>
						)}
						
						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}
						>
							<RCCInput
								name="filter" 
								label={translate("Pesquisar")}
								register={registerFilters}
								defaultValue={watchFilters("filter")}
							/>
						</RCCGrid>
					</RCCGrid>
				</FiltersContainer>
			)}	

			<Pagination 
				ref={paginationRef}
				dataOrRoute="Connections/List"
				filtersData={filters}   
				columns={[
					{
						idColumn: "efs001Usuario",
						nameColumn: translate("Usuário"),
						justify: "left",
						xs: 12,
						sm: 5,
						md: 5,
						cellRenderer: (row: any) => colUser(row),
						truncate: true
					},
					{
						idColumn: "efs001Remoteaddr",
						nameColumn: translate("IP"),
						justify: "left",
						xs: 12,
						sm: 2,
						md: 2,
						truncate: true,
						cellRenderer: colIp
					},
					{
						idColumn: "efs001Dtlogon",
						nameColumn: translate("Data LogOn"),
						justify: "left",
						xs: 12,
						sm: 2,
						md: 2,
						cellRenderer: (row: any) => formatDate(row.efs001Dtlogon,  currentLanguage, true),
						truncate: true
					},
					{
						idColumn: "efs001Dtultverificacao",
						nameColumn: translate("Data de expiração"),
						justify: "left",
						xs: 12,
						sm: 2,
						md: 2,
						cellRenderer: (row: any) => formatDate(row.efs001Dtultverificacao, currentLanguage, true),
						truncate: true
					},
					{
						idColumn: "options",
						nameColumn: "",
						justify: "center",
						xs: 12,
						sm: 1,
						md: 1,
						cellRenderer: colDelete,
						disableSort: true
					}
				]}
			/>

			<ModalHistory 
				ref={modalHistory}
			/>

			<ModalDescription 
				ref={modalDescriptionRef} 	
			/>

		</>
	);
};

export default SystemConnections;
